import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown } from 'lucide-react';

const FAQ_SECTIONS = [
  {
    title: 'General',
    items: [
      {
        q: 'What is {Beauty} Stashmart?',
        a: '{Beauty} Stashmart is a marketplace for beauty enthusiasts and influencers to sell beauty products — from personal stashes to PR products, brand gifts, and endorsement items. We connect real people, not stores. All products are sold by individual users.',
      },
      {
        q: 'Who can sell on {Beauty} Stashmart?',
        a: 'Any registered user aged 18 or older can sell on {Beauty} Stashmart. Beauty enthusiasts can sell from their personal collection, and influencers can also sell PR products, brand gifts, or endorsement items. Official stores, brand distributors, and wholesalers are not permitted.',
      },
      {
        q: 'Is {Beauty} Stashmart free to use?',
        a: 'Browsing and buying is free for buyers — you only pay the product price, payment processing fee, and shipping cost (no buyer-side platform fee). For sellers, we charge a 5% platform fee on each sale during our Beta, deducted from your payout. The listing form previews your net payout the moment you type a price.',
      },
    ],
  },
  {
    title: 'Buying',
    items: [
      {
        q: 'How do I buy a product?',
        a: 'Browse listings on the home feed, click on a product you like, then click "Buy Now" to add it to your cart. From the cart, select a shipping courier, and proceed to payment via Duitku. You can pay using Virtual Account (bank transfer), QRIS, or convenience store payment (Indomaret/Alfamart).',
      },
      {
        q: 'What payment methods are accepted?',
        a: 'We use Duitku as our payment gateway. Available methods include bank transfer (Virtual Account from BCA, BNI, BRI, Mandiri, and more), QRIS (scan to pay), and convenience store payments (Indomaret and Alfamart).',
      },
      {
        q: 'How do I know if a product is authentic?',
        a: 'Sellers are required to describe products honestly and accurately. We prohibit counterfeit or fake products. However, as a C2C marketplace, we recommend checking the seller\'s profile, reading the product description carefully, and contacting the seller with any questions before purchasing.',
      },
      {
        q: 'What happens after I pay?',
        a: 'After payment is confirmed, the seller is notified and must ship the item. You can track the order status from your Orders page. Once you receive the item, click "Confirm Received" to complete the transaction. If you don\'t confirm within 10 days after shipping, the order is auto-completed.',
      },
    ],
  },
  {
    title: 'Selling',
    items: [
      {
        q: 'How do I create a listing?',
        a: 'Click "Sell" in the navigation bar, fill in the product details (name, brand, condition, price, photos, description), and submit. Each listing is limited to 1 unit. Regular users can have up to 10 active listings, and verified influencers up to 25.',
      },
      {
        q: 'What can I sell?',
        a: 'You can sell beauty products from your personal stash — skincare, makeup, hair care, and body care items. This includes products you bought yourself, received as PR gifts, brand gifts, or endorsement items (see our PR product policy below). Products must not be expired, counterfeit, or hazardous, and you must describe the condition accurately (new, lightly used, half used, etc.).',
      },
      {
        q: 'What can I NOT sell?',
        a: 'Expired products, counterfeit or fake items, illegal or hazardous substances, and bulk or wholesale listings. You also cannot sell items explicitly marked "not for resale" by the brand, pre-release lab samples, or any product where your brand contract or agreement forbids resale.',
      },
      {
        q: 'Can I sell PR products, brand gifts, or endorsement items?',
        a: 'Yes — these are welcome on {Beauty} Stashmart as part of your personal stash, as long as there is no contract with the brand that forbids resale, the product is authentic, and it is safe and not expired. If an item is explicitly marked "not for resale," is a pre-release lab sample, or you know your agreement with the brand prohibits selling it, then it is not allowed.',
      },
      {
        q: 'When do I get paid?',
        a: 'Payment is processed through Duitku when the buyer completes payment. Funds are available after the buyer confirms receipt of the item or after the 10-day auto-completion period.',
      },
    ],
  },
  {
    title: 'Shipping',
    items: [
      {
        q: 'What shipping couriers are available?',
        a: 'We currently support JNE, J&T Express, and SiCepat. Shipping costs are calculated in real-time based on the buyer\'s and seller\'s locations.',
      },
      {
        q: 'How long does shipping take?',
        a: 'Shipping time depends on the courier and service selected. Estimated delivery times are shown when selecting a courier at checkout. Sellers must ship the item within 3 days of payment confirmation.',
      },
      {
        q: 'Can I meet up instead of shipping?',
        a: 'Currently, all transactions require courier shipping to ensure buyer and seller protection through our tracking system.',
      },
    ],
  },
  {
    title: 'Returns & Refunds (Beta)',
    items: [
      {
        q: 'Do you accept returns?',
        a: 'Yes, but only when it\'s clearly the seller\'s fault. During beta we accept return/refund requests for:\n\n\u2022 Wrong product sent\n\u2022 Missing items\n\u2022 Product damaged/leaking because of poor packing\n\u2022 Condition very different from the listing (e.g. claimed "swatched once" but clearly heavily used)\n\nWe don\'t accept returns for change of mind, scent/texture preferences, or shade mismatch when the listing was reasonably clear.',
      },
      {
        q: 'Who pays for shipping?',
        a: 'The buyer always pays shipping at checkout. This covers delivery from the seller to the buyer. Stashmart does not cover shipping or return shipping in beta.',
      },
      {
        q: 'What evidence do I need for a return?',
        a: 'Every return request must include a continuous unboxing video that:\n\n\u2022 Shows the unopened parcel\n\u2022 Clearly shows the shipping label (resi) on the package\n\u2022 Captures the entire unboxing and reveals the issue (wrong item, missing, damaged, etc.)\n\nReturn requests without this video will almost always be rejected. Clear photos and a screenshot of the original listing also help us decide faster.',
      },
      {
        q: 'Do I have to send the item back?',
        a: 'Sometimes yes, sometimes no:\n\n\u2022 For higher-value or re-sellable items, we may ask you to return the product to the seller using tracked shipping (paid by the buyer).\n\u2022 For low-value or hygiene-sensitive items, we may decide to refund without return and ask you to keep or safely discard the product.\n\nThis decision is made by Stashmart based on product type, value, and hygiene.',
      },
      {
        q: 'What happens to my money if a seller-fault return is approved?',
        a: 'If we confirm it\'s the seller\'s fault:\n\n\u2022 You get a full refund of what you paid for that order (product + shipping).\n\u2022 The seller does not get paid for that order.\n\nReturn shipping (if required) is still paid by the buyer.',
      },
      {
        q: 'Why is the policy strict in beta?',
        a: 'Stashmart is early-stage and currently charges a small 5% platform fee, so we don\'t have the budget to fund "try & return" shopping or free returns.\n\nOur priority is to:\n\n\u2022 Protect buyers from clear misrepresentation, and\n\u2022 Protect honest sellers from abuse,\n\nusing a simple, transparent system based on strong evidence (especially unboxing video). As we grow, we may expand buyer protection and return options.',
      },
    ],
  },
  {
    title: 'Account & Verification',
    items: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign In" and choose to register with your email. You\'ll receive a 6-digit verification code to confirm your email address. After verification, complete your profile with your name, date of birth, location, and a profile photo.',
      },
      {
        q: 'What is influencer verification?',
        a: 'Verified influencers get a special badge, can post video content (tutorials, reviews, GRWM, hauls), and have a higher listing limit (25 vs 10). To apply, check "I\'m a beauty influencer" on your profile and provide your social media handle and follower range. Verification is reviewed by our admin team.',
      },
      {
        q: 'Can I delete my account?',
        a: 'Yes, please contact us at hello@stashmart.store to request account deletion. All your data, listings, and order history will be permanently removed.',
      },
    ],
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-cream-100 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-cream-50/50 transition-colors"
        data-testid={`faq-q-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="font-body text-sm font-medium text-[#2C2A29] pr-4">{q}</span>
        <ChevronDown size={16} className={`text-[#A89F91] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 pt-0">
          <p className="font-body text-sm text-[#4A4745] leading-relaxed whitespace-pre-line">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  return (
    <div className="min-h-screen bg-[#FCFAF8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <span className="inline-block bg-[#FFF5F5] text-[#D48585] text-xs font-body font-semibold px-3 py-1 rounded-full mb-4">Help</span>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="faq-title">Frequently Asked Questions</h1>
        <p className="font-body text-sm text-[#A89F91] mb-8">Everything you need to know about {'{'}Beauty{'}'} Stashmart</p>

        <div className="space-y-8">
          {FAQ_SECTIONS.map(section => (
            <div key={section.title}>
              <h2 className="font-heading text-lg text-[#2C2A29] mb-3">{section.title}</h2>
              <div className="space-y-2">
                {section.items.map(item => (
                  <FaqItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-[#FFF5F5] rounded-2xl border border-[#F4C2C2]/30 text-center">
          <h3 className="font-heading text-lg text-[#2C2A29] mb-2">Still have questions?</h3>
          <p className="font-body text-sm text-[#4A4745] mb-4">
            We're here to help. Reach out to us anytime.
          </p>
          <a
            href="mailto:hello@stashmart.store"
            className="inline-block bg-[#F4C2C2] hover:bg-[#E8A0A0] text-[#2C2A29] font-body font-medium text-sm px-6 py-2.5 rounded-full transition-colors"
            data-testid="faq-contact-btn"
          >
            Contact us
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
